// Iter411 · Pestañas de gestión de proyecto reutilizables (Sprints / Hitos)
// para fichas que no son el ProjectDetail estándar (p.ej. ORA Zaragoza).
// Reuniones se cubre con <ProjectMeetings/>. Cada una es autosuficiente y
// solo necesita projectId.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, Trash2, GitBranch, Loader2, Target, Calendar, Flag, Edit2 } from "lucide-react";
import { API } from "@/context/AuthContext";
import { ProjectMilestones } from "@/components/project/ProjectPanels";

const SPRINT_STATUS = {
  planning:  { label: "Planificación", cls: "bg-gray-100 text-gray-700" },
  active:    { label: "Activo",        cls: "bg-blue-100 text-blue-800" },
  completed: { label: "Completado",    cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelado",     cls: "bg-rose-100 text-rose-800" },
};

// ── Sprints ──────────────────────────────────────────────────────────────────
export function ProjectSprintsTab({ projectId }) {
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const blank = { name: "", number: 1, status: "planning", start_date: "", end_date: "", goal: "" };
  const [form, setForm] = useState(blank);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/sprints`);
      setSprints(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Error cargando sprints");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const openNew = () => { setForm({ ...blank, number: sprints.length + 1 }); setShowModal(true); };

  const create = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      toast.error("Nombre, fecha inicio y fin son obligatorios"); return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/sprints`, { ...form, project_id: projectId, number: parseInt(form.number) || 1 });
      setSprints(prev => [...prev, data]);
      setShowModal(false);
      toast.success("Sprint creado");
    } catch (e) {
      toast.error("No se pudo crear el sprint");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (sid, status) => {
    setSprints(prev => prev.map(s => s.id === sid ? { ...s, status } : s));
    try { await axios.put(`${API}/sprints/${sid}`, { status }); }
    catch (e) { toast.error("No se pudo actualizar el estado"); load(); }
  };

  const remove = async (sid) => {
    if (!window.confirm("¿Eliminar este sprint?")) return;
    setSprints(prev => prev.filter(s => s.id !== sid));
    try { await axios.delete(`${API}/sprints/${sid}`); }
    catch (e) { toast.error("No se pudo eliminar"); load(); }
  };

  if (loading) return <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#007934]" size={24} /></div>;

  return (
    <div className="p-5 space-y-4" data-testid="ora-sprints-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">Gestión de Sprints</h2>
        <button onClick={openNew}
          className="flex items-center gap-2 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]"
          data-testid="add-sprint-btn">
          <Plus size={14} /> Nuevo Sprint
        </button>
      </div>

      {sprints.length === 0 ? (
        <div className="text-center py-14 text-gray-400" data-testid="sprints-empty">
          <GitBranch size={36} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">Sin sprints creados</p>
          <p className="text-xs mt-1">Crea los sprints del proyecto para planificar el trabajo por iteraciones</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sprints.map(s => {
            const cfg = SPRINT_STATUS[s.status] || SPRINT_STATUS.planning;
            return (
              <div key={s.id} className="bg-white border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition-all group" data-testid={`sprint-${s.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-[10px] font-mono text-gray-400">Sprint {s.number}</p>
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{s.name}</h3>
                  </div>
                  <button onClick={() => remove(s.id)} className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" data-testid={`delete-sprint-${s.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
                {s.goal && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{s.goal}</p>}
                <p className="text-[11px] text-gray-400 flex items-center gap-1 mb-2.5">
                  <Calendar size={10} />
                  {s.start_date || "—"} → {s.end_date || "—"}
                </p>
                <select value={s.status} onChange={e => updateStatus(s.id, e.target.value)}
                  className={`w-full text-xs rounded-lg px-2 py-1.5 border-0 font-medium ${cfg.cls} focus:outline-none focus:ring-1 focus:ring-[#007934]`}
                  data-testid={`sprint-status-${s.id}`}>
                  {Object.entries(SPRINT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="sprint-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">Nuevo Sprint</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Nombre *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="sprint-name-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Nº</label>
                <input type="number" value={form.number} onChange={e => setForm(f => ({ ...f, number: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="sprint-number-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Estado</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  {Object.entries(SPRINT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Inicio *</label>
                <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="sprint-start-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Fin *</label>
                <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="sprint-end-input" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Objetivo</label>
                <textarea value={form.goal} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowModal(false)} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
              <button onClick={create} disabled={saving}
                className="px-4 py-2 text-sm bg-[#007934] text-white rounded-lg hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2" data-testid="sprint-save-btn">
                {saving && <Loader2 size={14} className="animate-spin" />} Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hitos (Milestones) ─────────────────────────────────────────────────────────
export function ProjectMilestonesTab({ projectId }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit
  const blank = { title: "", description: "", due_date: "", status: "pending" };
  const [form, setForm] = useState(blank);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/milestones`);
      setMilestones(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Error cargando hitos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const openNew = () => { setForm(blank); setEditing({}); };
  const openEdit = (ms) => { setForm({ title: ms.title || "", description: ms.description || "", due_date: ms.due_date || "", status: ms.status || "pending" }); setEditing(ms); };

  const save = async () => {
    if (!form.title.trim() || !form.due_date) { toast.error("Título y fecha son obligatorios"); return; }
    setSaving(true);
    try {
      if (editing && editing.id) {
        const { data } = await axios.put(`${API}/milestones/${editing.id}`, form);
        setMilestones(prev => prev.map(m => m.id === editing.id ? data : m).sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")));
      } else {
        const { data } = await axios.post(`${API}/milestones`, { ...form, project_id: projectId });
        setMilestones(prev => [...prev, data].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")));
      }
      setEditing(null);
      toast.success("Hito guardado");
    } catch (e) {
      toast.error("No se pudo guardar el hito");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (mid) => {
    if (!window.confirm("¿Eliminar este hito?")) return;
    setMilestones(prev => prev.filter(m => m.id !== mid));
    try { await axios.delete(`${API}/milestones/${mid}`); }
    catch (e) { toast.error("No se pudo eliminar"); load(); }
  };

  const updateStatus = async (mid, status) => {
    setMilestones(prev => prev.map(m => m.id === mid ? { ...m, status } : m));
    try { await axios.put(`${API}/milestones/${mid}`, { status }); }
    catch (e) { toast.error("No se pudo actualizar"); load(); }
  };

  if (loading) return <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#007934]" size={24} /></div>;

  return (
    <div data-testid="ora-milestones-tab">
      <ProjectMilestones
        milestones={milestones}
        saving={saving}
        onAdd={openNew}
        onEdit={openEdit}
        onDelete={remove}
        onUpdateStatus={updateStatus}
      />

      {editing !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="milestone-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><Flag size={16} className="text-[#007934]" /> {editing.id ? "Editar hito" : "Nuevo hito"}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div>
              <label className="text-xs text-gray-500">Título *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="milestone-title-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Descripción</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Fecha *</label>
                <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="milestone-date-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Estado</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  <option value="pending">Pendiente</option>
                  <option value="in_progress">En progreso</option>
                  <option value="completed">Completado</option>
                  <option value="delayed">Retrasado</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
              <button onClick={save} disabled={saving}
                className="px-4 py-2 text-sm bg-[#007934] text-white rounded-lg hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2" data-testid="milestone-save-btn">
                {saving && <Loader2 size={14} className="animate-spin" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
